import { useEffect, useState } from "react";
import { PenLine, ShieldCheck, Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { signingConfigured } from "@/lib/signing";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function SignActions({ entityType, entityId, onChanged }) {
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // { mode: 'digital' | 'bankid', parties: [] }

  useEffect(() => {
    let m = true;
    signingConfigured().then((c) => m && setConfigured(c));
    return () => { m = false; };
  }, []);

  const base = entityType === "settlement" ? `/settlements/${entityId}` : `/agreements/${entityId}`;

  const digitalSign = async (party) => {
    const { data } = await api.post(`${base}/sign?party=${encodeURIComponent(party)}`);
    return data;
  };
  const bankidStart = async (party) => {
    const { data } = await api.post(`/signing/${entityType}/${entityId}/bankid`, { party });
    window.location.assign(data.signing_url);
  };

  const proceed = async (mode, parties, party) => {
    if (mode === "digital") {
      if (party === "__all__") {
        let executed = false;
        try {
          for (const p of parties) {
            const r = await digitalSign(p.key);
            if (r.executed) executed = true;
          }
          toast.success(executed ? "Ferdig signert – transaksjonen er gjennomført" : "Signert for alle dine parter");
        } finally {
          onChanged?.();
        }
      } else {
        const r = await digitalSign(party);
        toast.success(r.executed ? "Ferdig signert – transaksjonen er gjennomført" : "Signert. Venter på øvrige parter.");
        onChanged?.();
      }
    } else {
      await bankidStart(party === "__all__" ? parties[0].key : party); // redirect til BankID
    }
  };

  const onClick = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/signing/${entityType}/${entityId}/signable`);
      const parties = data.parties || [];
      if (parties.length === 0) {
        toast.error("Du kan ikke signere dette dokumentet");
      } else if (parties.length === 1) {
        await proceed(mode, parties, parties[0].key);
      } else {
        setDialog({ mode, parties });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const choose = async (party) => {
    const d = dialog;
    setDialog(null);
    setBusy(true);
    try {
      await proceed(d.mode, d.parties, party);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" className="gap-1.5" disabled={busy} onClick={() => onClick("digital")} data-testid={`sign-${entityType}-${entityId}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} Signer digitalt
      </Button>
      {configured ? (
        <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={() => onClick("bankid")} data-testid={`bankid-${entityType}-${entityId}`}>
          <ShieldCheck className="h-4 w-4" /> Signer med BankID
        </Button>
      ) : null}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="sign-party-dialog">
          <DialogHeader>
            <DialogTitle>Hvem vil du signere som?</DialogTitle>
            <DialogDescription>
              Du kan signere på vegne av flere parter i dette dokumentet{dialog?.mode === "bankid" ? " med BankID" : ""}. Velg én – eller signer for alle.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(dialog?.parties || []).map((p) => (
              <Button key={p.key} variant="outline" className="w-full justify-start gap-2" data-testid={`sign-party-option-${p.key}`} onClick={() => choose(p.key)}>
                <PenLine className="h-4 w-4 text-primary" /> {p.label}
              </Button>
            ))}
            {(dialog?.parties?.length || 0) > 1 ? (
              <Button className="w-full justify-start gap-2" data-testid="sign-party-all" onClick={() => choose("__all__")}>
                <Users className="h-4 w-4" /> Signer for alle mine parter
              </Button>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
